import type {
  AuthorityRef,
  CommandReceipt,
  SessionCreateInput,
  SessionDetail,
  SessionListQuery,
  SessionListResult,
  SessionRecoveryInput,
  SessionStartInput,
  SessionSummary,
  SessionWorkspace,
} from "./domain/session.ts";
import type { SyncStatus } from "./sync-status.ts";
import type { ProviderInventory } from "./provider-inventory.ts";
import type { AgentOperationInput, AgentOperationKind, AgentOperationPollOptions, AgentOperationReceipt } from "./agent-installations.ts";
import type { ProviderHistoryTransport } from "./provider-history.ts";

export interface SessionTransport extends ProviderHistoryTransport {
  readonly authority: AuthorityRef;
  /** Return a transport view pinned to a concrete resource authority. */
  forAuthority?(authority: AuthorityRef): SessionTransport;
  /** Durable queue/cursor telemetry shared by local and remote authorities. */
  getSyncStatus?(): Promise<SyncStatus>;
  getProviderInventory?(): Promise<ProviderInventory>;
  refreshProviderInventory?(): Promise<ProviderInventory>;
  /** Execute a typed Agent lifecycle operation on this Daemon. */
  agentOperation?(input: AgentOperationInput): Promise<AgentOperationReceipt>;
  /** Read the latest durable receipt for a previously accepted Agent operation. */
  getAgentOperationStatus?(
    commandId: string,
    operation: AgentOperationKind,
    deviceId?: string,
  ): Promise<AgentOperationReceipt>;
  /** Observe an Agent operation until terminal or the bounded poll timeout. */
  waitForAgentOperation?(
    commandId: string,
    operation: AgentOperationKind,
    options?: AgentOperationPollOptions,
    deviceId?: string,
  ): Promise<AgentOperationReceipt>;
  listSessions(query?: SessionListQuery): Promise<SessionListResult>;
  getSession(sessionId: string): Promise<SessionSummary>;
  getSessionWorkspace(sessionId: string): Promise<SessionWorkspace>;
  createSession(input: SessionCreateInput): Promise<SessionDetail>;
  startSession(sessionId: string, input?: SessionStartInput): Promise<CommandReceipt>;
  pauseSession(sessionId: string, waitForCompletion?: boolean): Promise<CommandReceipt>;
  recoverSession(sessionId: string, input?: SessionRecoveryInput): Promise<CommandReceipt>;
  stopSession(sessionId: string): Promise<CommandReceipt>;
  restartSession(sessionId: string, waitForCompletion?: boolean): Promise<CommandReceipt>;
  renameSession(sessionId: string, title: string): Promise<CommandReceipt>;
  deleteSession(sessionId: string): Promise<CommandReceipt>;
}

export type TransportErrorCode =
  | "INVALID_ARGUMENT"
  | "UNAUTHENTICATED"
  | "PERMISSION_DENIED"
  | "NOT_FOUND"
  | "CONFLICT"
  | "TIMEOUT"
  | "OFFLINE"
  | "UPGRADE_REQUIRED"
  | "UNSUPPORTED"
  | "UNAVAILABLE"
  | "CONTRACT_INVALID";

export class OrniTransportError extends Error {
  readonly code: TransportErrorCode;
  readonly retryable: boolean;
  readonly details?: Record<string, unknown>;

  constructor(
    code: TransportErrorCode,
    message: string,
    retryable = false,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "OrniTransportError";
    this.code = code;
    this.retryable = retryable;
    this.details = details;
  }
}
